import yaml from 'js-yaml';

/**
 * Parses a YAML string into a JavaScript object.
 * Throws an error if parsing fails.
 *
 * @param content The YAML string content.
 * @returns The parsed JavaScript object.
 */
export const parseYaml = (content: string): any => {
  try {
    const doc = yaml.load(content);
    // Basic validation: Check if it's an object (or potentially an array for some YAML structures)
    if (typeof doc !== 'object' || doc === null) {
      // Allow arrays as valid root YAML structures too
      if (!Array.isArray(doc)) {
        throw new Error('Parsed YAML is not a valid object or array.');
      }
    }
    return doc;
  } catch (e) {
    console.error('Failed to parse YAML:', e);
    // Re-throw the error or handle it as needed (e.g., return null, show notification)
    if (e instanceof Error) {
      throw new Error(`YAML Parsing Error: ${e.message}`);
    } else {
      throw new Error('An unknown error occurred during YAML parsing.');
    }
  }
};

/**
 * Converts a JavaScript object into a YAML string.
 *
 * @param data The JavaScript object to convert.
 * @param options Optional js-yaml dump options (e.g., indent, sortKeys).
 * @returns The generated YAML string.
 */
export const generateYaml = (data: any, options?: yaml.DumpOptions): string => {
  try {
    // Default options for better readability
    const defaultOptions: yaml.DumpOptions = {
      indent: 2,
      lineWidth: -1, // No line wrapping
      noRefs: true, // Avoid YAML references for simpler output
      sortKeys: false, // Preserve original key order if possible
      ...options, // Allow overriding defaults
    };
    return yaml.dump(data, defaultOptions);
  } catch (e) {
    console.error('Failed to generate YAML:', e);
    if (e instanceof Error) {
      return `# YAML Generation Error: ${e.message}`;
    } else {
      return '# An unknown error occurred during YAML generation.';
    }
  }
};
